<template>
  <q-page
    :class="{ 'preview-page': (!!previewPage && showChangeIndicator), 'change-indicator-page': showChangeIndicator, [displayPage.scopeClass]: true }"
    :style="editMode ? '' : ''"
    data-test-id="custom-page"
  >
    <q-inner-loading :showing="page.loading" />
    <transition-group
      v-if="finalPage"
      name="list-complete"
      style="display: block;"
      tag="span"
    >
      <div
        v-for="section in $_.filter(displayPage.sections.data, section => editMode || section.visible)"
        :key="section.draftId || section.id"
        :class="(shouldAnimate ? 'list-complete-item' : '') + ' ' + (editMode ? 'edit-section' : '')"
        data-test-id="section"
        @dblclick="handleEditRequest(section)"
      >
        <section-edit-controls
          v-if="editMode"
          :section="section"
          @edit="editSection(section)"
          @edit-css="editCss(section)"
          @set-order="setOrder"
          @duplicate="duplicateSection(section)"
          @delete="deleteSection(section)"
          @undo-delete="undelete(section)"
        />
        <div
          v-if="section.showOnScreen($q.screen)"
          :class="{
            [section.class]: true,
            'list-complete-item': shouldAnimate
          }"
          :style="section.backgroundStyle"
          data-test-id="section-content"
        >
          <section-component
            :section="section"
            :style="section.style"
            :demo-mode="previewChanges && !!previewPage"
            :edit-mode="editMode"
            @show-site-selector-drawer="$emit('show-site-selector-drawer')"
          />
        </div>
        <section-edit-menu
          v-if="isPageEditingAllowed"
          :section="section"
          :edit-mode="editMode"
          @edit="editSection(section)"
          @edit-css="editCss(section)"
          @enable-edit-mode="showChangeIndicator = true; previewChanges = true; editMode = true"
          @page-options="$refs.pageForm.show(previewPage)"
          @set-order="setOrder"
        />
      </div>
      <div
        v-if="editMode"
        key="add-section"
        class="row no-wrap justify-center items-end q-gutter-sm edit-section"
        data-test-id="add-section-controls"
      >
        <q-btn
          class="col-auto q-my-md"
          outline
          color="positive"
          size="sm"
          label="Add Section"
          @click="addSection()"
        />
      </div>
    </transition-group>
    <template v-if="showChangeIndicator && previewPage && previewPage.isRemote">
      <page-preview-controls
        :page="page"
        :edit-mode="editMode"
        :preview-changes="previewChanges"
        :discarding-draft="discardingDraft"
        :publishing="publishing"
        @toggle-preview="handlePreviewControlsTogglePreview"
        @toggle-edit-mode="handlePreviewControlsToggleEditMode"
        @edit="$refs.pageForm.show(previewPage)"
        @set-order="setOrder"
        @time-travel="$emit('time-travel')"
        @stop-time-travel="$emit('stop-time-travel')"
        @edit-css="editCss()"
        @close="close"
        @discard-draft="discardDraft"
        @publish="publish"
      />
      <div class="edit-highlight fixed-full" />
      <q-inner-loading
        :showing="displayPage.loading"
      />
    </template>
    <page-form
      v-if="previewPage"
      ref="pageForm"
      @change="handleChange"
    />
    <section-form
      v-if="showChangeIndicator"
      ref="sectionForm"
      :page="page"
      @change="handleChange"
    />
    <set-display-order-dialog
      v-if="showChangeIndicator"
      ref="orderDialog"
      :list="previewPage.sections.data"
      @reorder="handleReorder"
    />
  </q-page>
</template>

<script>
import { isNative } from 'src/config'
import Model from 'src/models/Model'
import { SECTION_TYPES } from 'src/models/page/Section.js'
import SectionComponent from 'src/modules/consumer/pages/SectionComponent'
import { defineAsyncComponent } from 'vue'

export default {
  name: 'CustomPage',
  components: {
    PageForm: defineAsyncComponent(() => import('src/modules/admin/page/PageForm')),
    PagePreviewControls: defineAsyncComponent(() => import('src/modules/consumer/components/PagePreviewControls')),
    SectionComponent,
    SectionEditControls: defineAsyncComponent(() => import('src/modules/consumer/components/SectionEditControls')),
    SectionEditMenu: defineAsyncComponent(() => import('src/modules/consumer/components/SectionEditMenu')),
    SectionForm: defineAsyncComponent(() => import('src/modules/admin/section/SectionForm')),
    SetDisplayOrderDialog: defineAsyncComponent(() => import('src/modules/admin/components/SetDisplayOrderDialog'))
  },
  configuration: ['currentSite', 'publishedPages', 'publishedNewsItems', 'navigations'],
  context: ['currentUser'],
  meta () {
    return {
      title: this.page.title,
      meta: {
        description: { name: 'description', content: this.page.searchEngineSnippet || this.currentSite.siteDescription || 'Check showtimes and buy tickets at your local cinema' }
      }
    }
  },
  emits: ['show-site-selector-drawer', 'time-travel', 'stop-time-travel', 'edit-css', 'stop-edit-css'],
  data () {
    return {
      showChangeIndicator: false,
      previewChanges: false,
      editMode: false,
      editingSection: null,
      publishing: false,
      discardingDraft: false
    }
  },
  models: {
    page: {
      sections: {
        showingIds: true,
        movieIds: true,
        newsItemClassIds: true,
        titleClassIds: true,
        exploreTitleClassIds: true,
        exploreFeaturedTitleClassIds: true,
        showingBadgeIds: true,
        exploreFeaturedShowingBadgeIds: true,
        filterShowingBadgeIds: true,
        filterOptionShowingBadgeIds: true,
        content: true,
        scopeClass: true
      },
      transformedStyles: true,
      scopeClass: true,
      describeChanges: true,
      variables () {
        return {
          id: this.finalPage?.redirect ? null : this.finalPage?.id
        }
      },
      immediate: true
    },
    previewPage: {
      sections: {
        showingIds: true,
        movieIds: true,
        newsItemClassIds: true,
        titleClassIds: true,
        exploreTitleClassIds: true,
        exploreFeaturedTitleClassIds: true,
        showingBadgeIds: true,
        exploreFeaturedShowingBadgeIds: true,
        filterShowingBadgeIds: true,
        filterOptionShowingBadgeIds: true,
        content: true,
        styles: true,
        transformedStyles: true,
        scopeClass: true
      },
      styles: true,
      transformedStyles: true,
      scopeClass: true,
      variables () {
        return {
          id: this.page?.id,
          skip: !this.currentUser || !this.currentUser.admin
        }
      }
    }
  },
  computed: {
    displayPage () {
      return (this.previewChanges && this.previewPage) ? this.previewPage : this.page
    },
    isNative () {
      return isNative
    },
    isPageEditingAllowed () {
      return !!this.currentUser?.admin && this.page.isManagePermitted
    },
    shouldAnimate () {
      return this.editMode || this.editMode || this.previewChanges || this.showChangeIndicator || this.publishing || this.currentSite.previewTime
    }
  },
  watch: {
    isPageEditingAllowed () {
      if (!this.isPageEditingAllowed && (this.editMode || this.previewChanges || this.showChangeIndicator)) {
        this.close()
      }
    },
    'page.describeChanges' () {
      if (this.page.describeChanges && this.isPageEditingAllowed) {
        this.showChangeIndicator = true
        this.previewChanges = true
      }
    },
    'page.visible' () {
      this.validateVisible()
    },
    'page.id' () {
      this.validateVisible()
    },
    'displayPage.transformedStyles': {
      handler () {
        this.$mh.setCustomPageStyles(this.displayPage.transformedStyles)
      },
      immediate: true
    }
  },
  beforeCreate () {
    // Before we create this page, we want to lock in the final page given the slug
    this.finalPage = this.publishedPages?.find(page => page.slug === this.$route.params.slug)
  },
  created () {
    _.extend(this, { SECTION_TYPES })
  },
  unmounted () {
    this.$mh.setCustomPageStyles(null)
  },
  methods: {
    validateVisible () {
      if (!_.endsWith(this.$router.currentRoute.name, ':custom-page') && !_.endsWith(this.$router.currentRoute.name, ':home-page')) return

      if (this.editMode) return

      if (this.page.visible) return

      // Handle the case where the page we are on is no longer visible
      this.$router.push(this.publishedPages.homePage.route)
    },
    async editSection (section) {
      if (!this.isPageEditingAllowed) return

      if (section.draftId) {
        section = _.find(this.previewPage.data.sections.data, { id: section.draftId })
      }
      this.showChangeIndicator = true
      this.previewChanges = true

      await this.$nextTick()

      this.$refs.sectionForm?.show(section)
    },
    addSection () {
      this.$refs.sectionForm.show(null, { pageId: this.previewPage.id })
    },
    async deleteSection (section) {
      await section.delete()
      this.handleChange()
    },
    setOrder () {
      this.$refs.orderDialog.show()
    },
    async discardDraft () {
      if (!this.isPageEditingAllowed) {
        this.close()
        return
      }

      this.discardingDraft = true

      await this.page.discardDraft()
      await this.previewPage.fetch(true)

      this.discardingDraft = false

      this.close()
    },
    async publish () {
      if (!this.isPageEditingAllowed) {
        this.close()
        return
      }

      this.publishing = true

      await this.page.publish()
      await this.previewPage.fetch(true)
      await this.page.fetch(true)

      this.publishing = false

      this.close()
    },
    handleChange () {
      this.page.fetch(true)
      this.previewPage.fetch(true)
    },
    handleEditRequest (section) {
      if (!this.isPageEditingAllowed) return

      this.showChangeIndicator = true
      this.previewChanges = true
      this.editMode = true

      this.editSection(section)
    },
    async handleReorder ({ reorderedList }) {
      this.previewPage.sections.data = reorderedList
      this.previewPage.sections.validateDisplayOrder()

      this.previewPage.sections.loading = true
      // This prevents the subscription trigger from showing partial results and strange sorting
      this.previewPage.sections.silent = true

      // Save all models
      await Model.configuration.batchSave(async () => {
        const saveMutations = _(this.previewPage.sections.data).filter({ isDirty: true }).map(model => model.save()).value()
        await Promise.all(saveMutations)
      })

      this.previewPage.sections.loading = false
      this.previewPage.sections.silent = false

      this.page.fetch(true)
    },
    handlePreviewControlsTogglePreview () {
      if (!this.isPageEditingAllowed) return

      this.previewChanges = !this.previewChanges
      if (!this.previewChanges) {
        this.editMode = false
      }
    },
    handlePreviewControlsToggleEditMode () {
      if (!this.isPageEditingAllowed) return

      this.editMode = !this.editMode
      if (this.editMode) {
        this.previewChanges = true
      }
    },
    async undelete (section) {
      await section.undelete()

      this.handleChange(true)
    },
    async duplicateSection (section) {
      await section.duplicate()

      this.handleChange(true)
    },
    editCss (section) {
      if (section?.draftId) {
        section = _.find(this.previewPage.data.sections.data, { id: section.draftId })
      }

      this.showChangeIndicator = true
      // Remove all the trappings so it looks closer to the final version while editing the css
      this.editMode = false
      // Editing page CSS only works for preview mode
      this.previewChanges = true

      this.$emit('edit-css', this.previewPage, section, this.handleChange)
    },
    close () {
      this.previewChanges = false
      this.editMode = false
      this.showChangeIndicator = false
      this.$emit('stop-edit-css')
    }
  },
  trackPage () {
    if (!this.$consumer || !this.finalPage) return

    return {
      id: this.finalPage.id,
      name: this.finalPage.name,
      pageId: this.finalPage.id,
      title: this.finalPage.title
    }
  }
}
</script>

<style lang="scss" scoped>
.page {
  width: 100%;
  margin: 0 auto 0;
  padding: 10px 5px;
  overflow: hidden;
}

.change-indicator-page {
  margin-bottom: 120px;
}
.edit-highlight {
  box-shadow: inset 0 0 7px 0 #000;
  border: solid 4px $negative;
  pointer-events: none;
  z-index: 100000;
}
.edit-section {
  padding: 10px;
  margin: 20px 15px;
  border: dashed 3px $warning;
}

.list-complete-item {
  transition: all 0.5s;
}
.list-complete-enter-from {
  opacity: 0;
  transform: translateX(0) translateY(0);
}
.list-complete-leave-to {
  opacity: 0;
  transform: translateX(0) translateY(0);
}
.list-complete-leave-active {
  position: absolute;
}
</style>
